import { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import Sidebar from "@/components/portal/Sidebar";
import Header from "@/components/portal/Header";
import MyPolicies from "@/components/portal/MyPolicies";
import MySplits from "@/components/portal/MySplits";
import MyCommissions from "@/components/portal/MyCommissions";
import CommissionTracker from "@/components/portal/CommissionTracker";
import CommissionSummary from "@/components/portal/CommissionSummary";
import Downlines from "@/components/portal/Downlines";
import Leaderboard from "@/components/portal/Leaderboard";
import AdminContracting from "@/components/portal/AdminContracting";
import AdminUserManagement from "@/components/portal/AdminUserManagement";
import MasterUserSettings from "@/components/portal/MasterUserSettings";
import MasterLocationsPIT from "@/components/portal/MasterLocationsPIT";
import DebtRecovery from "@/components/portal/DebtRecovery";
import DebtMonitoring from "@/components/portal/DebtMonitoring";
import MarketingHub from "@/components/portal/MarketingHub";
import ProfileSettings from "@/components/portal/ProfileSettings";
import MyTickets from "@/components/portal/MyTickets";
import Tools from "@/components/portal/Tools";
import SupportTickets from "@/components/portal/SupportTickets";
import IdeaSummary from "@/components/portal/IdeaSummary";
import ComingSoon from "@/components/portal/ComingSoon";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Portal() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [userInfo, setUserInfo] = useState(null);
  const [activeSection, setActiveSection] = useState('my-policies');
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  useEffect(() => {
    // Check if user is authenticated
    const storedUserInfo = localStorage.getItem('user_info');
    if (!storedUserInfo) {
      // Check if URL params are present, if so redirect to verification flow
      const locationId = searchParams.get('location_id');
      const userId = searchParams.get('user_id');
      
      if (locationId && userId) {
        // Redirect to verification page with params
        navigate(`/?location_id=${locationId}&user_id=${userId}`);
      } else {
        toast.error('Please complete verification first');
        navigate('/');
      }
      return;
    }
    
    try {
      const parsed = JSON.parse(storedUserInfo);
      setUserInfo(parsed);
    } catch (err) {
      console.error('Error parsing user info:', err);
      navigate('/');
    }
  }, [navigate, searchParams]);

  if (!userInfo) {
    return null;
  }

  const renderContent = () => {
    switch (activeSection) {
      case 'my-policies':
        return <MyPolicies userInfo={userInfo} />;
      case 'leaderboard':
        return <Leaderboard userInfo={userInfo} />;
      case 'my-splits':
        return <MySplits userInfo={userInfo} />;
      case 'my-commissions':
        return <MyCommissions userInfo={userInfo} />;
      case 'debt-recovery':
        return <DebtRecovery userInfo={userInfo} />;
      case 'commission-tracker':
        return <CommissionTracker userInfo={userInfo} />;
      case 'commission-summary':
        return <CommissionSummary userInfo={userInfo} />;
      case 'debt-monitoring':
        return <DebtMonitoring userInfo={userInfo} />;
      case 'downlines':
        return <Downlines userInfo={userInfo} />;
      case 'agency':
        return <ComingSoon title="Agency" />;
      // Profile
      case 'profile-settings':
        return <ProfileSettings userInfo={userInfo} />;
      // Resources
      case 'marketing-hub':
        return <MarketingHub userInfo={userInfo} />;
      case 'tools':
        return <Tools userInfo={userInfo} />;
      case 'my-tickets':
        return <MyTickets userInfo={userInfo} />;
      case 'ideas':
        return <ComingSoon title="Ideas" />;
      // Admin Settings
      case 'admin-contracting':
        return <AdminContracting />;
      case 'admin-user-management':
        return <AdminUserManagement userInfo={userInfo} />;
      // Master Settings
      case 'master-user-settings':
        return <MasterUserSettings />;
      case 'master-locations-pit':
        return <MasterLocationsPIT />;
      // Support (Internal Management)
      case 'support-tickets':
        return <SupportTickets userInfo={userInfo} />;
      case 'idea-summary':
        return <IdeaSummary />;
      default:
        return <MyPolicies userInfo={userInfo} />;
    }
  };

  return (
    <div className="flex h-screen bg-neutral-50 dark:bg-neutral-950">
      <Sidebar
        activeSection={activeSection}
        setActiveSection={setActiveSection}
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
        userInfo={userInfo}
      />
      <div className="flex-1 flex flex-col overflow-hidden">
        <Header userInfo={userInfo} setActiveSection={setActiveSection} />
        <main className="flex-1 overflow-y-auto p-6 pb-[10vh] dark:bg-neutral-950">
          {renderContent()}
        </main>
      </div>
    </div>
  );
}